import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Cog6ToothIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { formatEuro, calcolaProvvigioni, prezzoNegozioConIva } from './statiPratica';

export default function BikeImpostazioni() {
  const [perc, setPerc] = useState('30');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/bici/impostazioni');
        setPerc(String(data.percentualeProvvigione ?? 30));
        setNote(data.note || '');
      } catch (e) { toast.error('Errore nel caricamento impostazioni'); }
      finally { setLoading(false); }
    })();
  }, []);

  const salva = async () => {
    setSalvando(true);
    try {
      await api.put('/bici/impostazioni', { percentualeProvvigione: parseFloat(perc) || 0, note });
      toast.success('Impostazioni salvate');
    } catch (err) { toast.error(err.response?.data?.error || 'Errore nel salvataggio'); }
    finally { setSalvando(false); }
  };

  // esempio dimostrativo (prezzo negozio 950 no-IVA → +22% IVA)
  const negozioEsempio = prezzoNegozioConIva(950);
  const esempio = calcolaProvvigioni(1500, negozioEsempio, parseFloat(perc) || 0);

  if (loading) return <div className="max-w-[900px] mx-auto px-4 md:px-6 py-16 text-center text-gray-400">Caricamento…</div>;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-gray-50/50 via-white to-emerald-50/30">
      <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Header */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50 p-6">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex items-center gap-2">
            <Cog6ToothIcon className="h-7 w-7 text-emerald-600" /> Impostazioni
          </h1>
          <p className="mt-2 text-gray-600">Configura le regole di provvigione del modulo bici.</p>
        </div>

        {/* Provvigioni */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50 p-6">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-1"><BanknotesIcon className="h-5 w-5 text-emerald-600" /> Provvigioni agenti</h3>
          <p className="text-sm text-gray-500 mb-5">
            Il <b>margine</b> di ogni vendita è la differenza tra <b>Prezzo pubblico</b> (quanto vendiamo, IVA inclusa) e
            <b> Prezzo negozio maggiorato del 22% di IVA</b> (il costo è memorizzato IVA esclusa), e rappresenta il 100% della provvigione.
            L'agente riceve la percentuale fissa qui sotto.
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Percentuale agente sul margine</label>
              <div className="relative">
                <input type="number" min="0" max="100" step="0.5" value={perc} onChange={e => setPerc(e.target.value)}
                  className="w-full pr-10 pl-4 py-3 bg-white/70 border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all text-lg font-semibold" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">%</span>
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 mt-4">Note (opzionale)</label>
              <textarea rows="3" value={note} onChange={e => setNote(e.target.value)}
                className="w-full px-4 py-2.5 bg-white/70 border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all text-sm" />
            </div>

            {/* Esempio */}
            <div className="bg-gray-50/70 border border-gray-100 rounded-xl p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-3">Esempio di calcolo</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Prezzo pubblico</span><span className="font-medium">{formatEuro(1500)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Prezzo negozio (IVA incl.)</span><span className="font-medium">{formatEuro(negozioEsempio)}</span></div>
                <div className="flex justify-between border-t border-gray-200 pt-2"><span className="text-gray-500">Margine (100%)</span><span className="font-semibold">{formatEuro(esempio.margine)}</span></div>
                <div className="flex justify-between bg-emerald-50 -mx-2 px-2 py-1.5 rounded-lg mt-1"><span className="text-emerald-700">Provvigione agente ({esempio.percentuale}%)</span><span className="font-bold text-emerald-700">{formatEuro(esempio.provvigioneAgente)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Quota negozio</span><span className="font-medium">{formatEuro(esempio.provvigioneAzienda)}</span></div>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button onClick={salva} disabled={salvando}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-medium shadow-lg shadow-emerald-500/25 hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50">
              {salvando ? 'Salvataggio…' : 'Salva impostazioni'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
